import { useCallback, useMemo } from 'react'

import { useNavigate } from 'Pods/Shared/ContextBasedRouter'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useGetLibraryItems from 'Pods/ThreeDStudio/Components/Sidebar/Components/LibraryContent/Hooks/useGetLibraryItems'
import { LIBRARY_TAB, buildImagePath } from 'Pods/ThreeDStudio/constants'

// Only finished images have a page of their own to step to; requested and processing runs in
// the list are skipped.
const isNavigable = attributes => attributes?.status === 'succeeded' && !!attributes?.slug

const toSlugs = items => items
  .map(item => item?.attributes)
  .filter(isNavigable)
  .map(attributes => attributes.slug)

// Previous / next for an image opened at /ai-image/:slug, over the viewer's My Assets > Images
// list in its order. Reads the same unfiltered query the sidebar's Images badge does, so a list
// the sidebar has loaded costs no request; it does not poll, the sidebar's own query does.
// An image the list does not hold (another user's, or one past an unloaded page) has no
// position, so both directions stay off. On the last loaded image, next pulls in the following
// page and steps into it, as the community gallery's modal does. `listedAttributes` is the
// opened image's own list entry, so the modal can show it before its full payload arrives.
function useLibraryImageNavigation(slug) {
  const navigate = useNavigate()
  const isLoggedIn = usePageConfig()?.isCurrentUser === true
  const {
    items, hasNextPage, isFetchingNextPage, handleLoadMore,
  } = useGetLibraryItems(isLoggedIn && !!slug, '', LIBRARY_TAB.IMAGES, false)

  const slugs = useMemo(() => toSlugs(items), [items])
  const listedAttributes = useMemo(() => {
    if (!slug) return null

    return items
      .map(item => item?.attributes)
      .find(attributes => isNavigable(attributes) && attributes.slug === slug) ?? null
  }, [items, slug])
  const index = slug ? slugs.indexOf(slug) : -1
  const isListed = index !== -1
  const hasPrevious = index > 0
  const hasLoadedNext = isListed && index < slugs.length - 1
  const hasNext = hasLoadedNext || (isListed && hasNextPage)

  const open = useCallback((target) => {
    if (target) navigate(buildImagePath(target))
  }, [navigate])

  const previous = useCallback(() => {
    if (hasPrevious) open(slugs[index - 1])
  }, [hasPrevious, slugs, index, open])

  const next = useCallback(async () => {
    if (hasLoadedNext) {
      open(slugs[index + 1])
      return
    }
    if (!isListed || !hasNextPage || isFetchingNextPage) return

    const result = await handleLoadMore()
    const appended = toSlugs(result?.data?.pages?.flatMap(page => page.items) ?? [])

    open(appended[index + 1])
  }, [hasLoadedNext, isListed, hasNextPage, isFetchingNextPage, handleLoadMore, slugs, index, open])

  return {
    hasPrevious, hasNext, previous, next, listedAttributes,
  }
}

export default useLibraryImageNavigation
